import React from "react";
import PropTypes from "prop-types";
import { Box, Link } from "@mui/material/";

export const WEIXIN_FOOTNOTE_ID = "faq-weixin-tab-disclosure";
export const GRAB_FOOTNOTE_ID = "faq-grab-tab-disclaimer";

const FOOTNOTE_MARKER_PATTERN = /\*+/;

export const splitFootnoteMarker = (text) => {
  if (typeof text !== "string") return { text: text ?? "", marker: null };
  const match = text.match(/^(.*?)(\*+)$/);
  if (!match) return { text, marker: null };
  return { text: match[1], marker: match[2] };
};

export const hasFootnoteMarker = (text) =>
  typeof text === "string" && FOOTNOTE_MARKER_PATTERN.test(text);

export const FOOTNOTE_SCROLL_OFFSET_PX = 80;

const getDocumentOffsetTop = (el) => {
  let top = 0;
  let node = el;
  while (node) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return top;
};

export const FootnoteReference = ({ marker, footnoteId, sx }) => {
  const handleClick = (event) => {
    const target = document.getElementById(footnoteId);
    if (!target) return;

    event.preventDefault();
    // Use offsetTop so AOS transforms don't pull the target past the viewport.
    const top = getDocumentOffsetTop(target) - FOOTNOTE_SCROLL_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    target.focus({ preventScroll: true });
    window.history.replaceState(null, "", `#${footnoteId}`);
  };

  return (
    <Box
      component="sup"
      sx={sx}
    >
      <Link
        href={`#${footnoteId}`}
        underline="none"
        color="inherit"
        onClick={handleClick}
        sx={{
          fontWeight: "inherit",
          verticalAlign: "baseline",
          color: "inherit",
          "&:hover, &:focus, &:visited, &:active, &.Mui-focusVisible": {
            color: "inherit",
          },
        }}
      >
        {marker}
      </Link>
    </Box>
  );
};

FootnoteReference.propTypes = {
  marker: PropTypes.string.isRequired,
  footnoteId: PropTypes.string.isRequired,
  sx: PropTypes.object,
};

export const renderDescribedByMarker = (text) => {
  const { text: baseText, marker } = splitFootnoteMarker(text);
  if (!marker) return text;

  return (
    <>
      {baseText}
      <Box component="sup">{marker}</Box>
    </>
  );
};

export const renderFootnotedText = (text, footnote) => {
  if (!footnote?.id || typeof text !== "string") return text;
  if (!hasFootnoteMarker(text)) return text;

  return text.split(/(\*+)/).map((part, index) => {
    if (!part) return null;
    if (/^\*+$/.test(part)) {
      return (
        <FootnoteReference
          key={`fn-${index}`}
          marker={part}
          footnoteId={footnote.id}
        />
      );
    }
    return part;
  });
};

export const TabDisclaimer = ({ id, marker, ariaLabel, children, className }) => (
  <Box
    id={id}
    component="aside"
    role="note"
    aria-label={ariaLabel}
    tabIndex={-1}
    className={className}
    sx={{ scrollMarginTop: `${FOOTNOTE_SCROLL_OFFSET_PX}px` }}
  >
    {marker ? <Box component="span">{marker} </Box> : null}
    {children}
  </Box>
);

TabDisclaimer.propTypes = {
  id: PropTypes.string.isRequired,
  marker: PropTypes.string,
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
